import {
  ArrowDownTrayIcon,
  ArrowRightIcon,
  ArrowTopRightOnSquareIcon,
  BuildingOffice2Icon,
  CalendarDaysIcon,
  ChartBarIcon,
  ChatBubbleBottomCenterTextIcon,
  CheckCircleIcon,
  CurrencyEuroIcon,
  DocumentDuplicateIcon,
  DocumentTextIcon,
  EyeIcon,
  HomeModernIcon,
  NewspaperIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useBlocker, useNavigate } from 'react-router-dom';

import type {
  AgCommunicationStrategy,
  Company,
  DpeFirstContactTemplateKey,
  DpeGeneralMailTemplateKey,
  ReferentCampaignTemplateKey,
} from '../../../@types/Company';
import type { ReferentCampaignIndex, ReferentCampaignWindow } from '../../../@types/ReferentCampaign';
import type { SciMailKind, SciMailWindow, SciMailWindowsMap } from '../../../@types/SciMail';
import { buildSciMailWindowKey } from '../../../@types/SciMail';
import { refreshCompanyData, updateCompanyBranding } from '../../../store/AuthReducer';
import type { RootState } from '../../../store/store';
import { getCompanyReferentCampaignWindows, getCompanySciMailWindows } from '../../../utils/api';
import { resolveTimelineRowVisibility } from '../../../utils/boitageTimeline';
import { getValidReferents } from '../../../utils/branding';
import classNames from '../../../utils/classNames';
import { JWTAuthorization, JWTHeader } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import BoitageDocumentsPanel from '../../Analyzing/BoitageQualification/components/BoitageDocumentsPanel.module';
import { showToast } from '../../common/Notification';
import Popup from '../../common/Popup';
import type { CustomDocFile } from '../../Communication/ReferentCampaigns/utils/referentCampaignCustomDoc';
import {
  deleteCustomDoc,
  downloadCustomDoc,
  getCustomDocForCampaign,
  uploadCustomDoc,
  validateCustomDocFile,
} from '../../Communication/ReferentCampaigns/utils/referentCampaignCustomDoc';
import { downloadReferentCampaignAsPDFOrZip } from '../../Communication/ReferentCampaigns/utils/referentCampaignPDF';
import type { ModelId, VersionId } from '../../Communication/ReferentCampaigns/utils/referentCampaignTemplates';
import {
  buildTemplateKey,
  BUILTIN_MODEL_IDS,
  CUSTOM_MODEL_ID,
  CUSTOM_TEMPLATE_KEY,
  CUSTOM_VERSION_ID,
  DEFAULT_REFERENT_CAMPAIGN_EMAIL_TEXT,
  isCustomModelId,
  parseTemplateKey,
  tabsConfig,
  VERSION_ORDER,
  versionLabels,
} from '../../Communication/ReferentCampaigns/utils/referentCampaignTemplates';
import {
  DPE_GENERAL_MAIL_TEMPLATES,
  GENERAL_MAIL_STANDARD_OPTIONS,
  GENERAL_MAIL_TRANSACTION_OPTIONS,
} from '../../Prospecting/OwnerCommunication/components/DpeGeneralMailings/dpeGeneralMailContent';
import type { FirstContactTemplateType } from '../../Prospecting/OwnerCommunication/utils/emailContentGenerator';
import {
  FIRST_CONTACT_DPE_OPTIONS,
  FIRST_CONTACT_TEMPLATE,
  FIRST_CONTACT_TEMPLATE_DESCRIPTIONS,
  FIRST_CONTACT_TEMPLATE_LABELS,
  FIRST_CONTACT_TRANSACTION_OPTIONS,
} from '../../Prospecting/OwnerCommunication/utils/emailContentGenerator';
import { rentMailTemplates } from '../../Prospecting/SCIsRent/rentMailTemplates';
import { transactionMailTemplates } from '../../Prospecting/SCIsTransaction/transactionMailTemplates';
import type { PreviewTarget } from './PreviewModal';
import PreviewModal from './PreviewModal';

const FIRST_CONTACT_ICONS: Record<FirstContactTemplateType, React.ElementType> = {
  [FIRST_CONTACT_TEMPLATE.REFERENT_ONLY]: ChatBubbleBottomCenterTextIcon,
  [FIRST_CONTACT_TEMPLATE.DPE_REPORT]: ChartBarIcon,
  [FIRST_CONTACT_TEMPLATE.COPRO_REPORT]: BuildingOffice2Icon,
  [FIRST_CONTACT_TEMPLATE.BOTH_REPORTS]: DocumentDuplicateIcon,
  [FIRST_CONTACT_TEMPLATE.TRANSACTION_V1]: HomeModernIcon,
  [FIRST_CONTACT_TEMPLATE.TRANSACTION_V2]: CurrencyEuroIcon,
};

const GENERAL_MAIL_TRANSACTION_ICONS: Record<'transaction-v1' | 'transaction-v2', React.ElementType> = {
  'transaction-v1': HomeModernIcon,
  'transaction-v2': CurrencyEuroIcon,
};

const SCI_RENT_KEY_PREFIX = 'rent-';
const SCI_TRANSACTION_KEY_PREFIX = 'transaction-';

const buildSciRentKey = (id: number): string => `${SCI_RENT_KEY_PREFIX}${id}`;
const buildSciTransactionKey = (id: number): string => `${SCI_TRANSACTION_KEY_PREFIX}${id}`;
const parseSciKeyId = (key: string | null | undefined): number | null => {
  if (!key) {return null;}
  const match = /-(\d+)$/.exec(key);
  return match ? Number.parseInt(match[1], 10) : null;
};

type ReferentCampaignDraft = Record<ReferentCampaignIndex, ReferentCampaignTemplateKey | undefined>;

interface ContentDraft {
  defaultDpeFirstContactTemplate: DpeFirstContactTemplateKey | undefined;
  referentCampaigns: ReferentCampaignDraft;
  referentCampaignEmailText: string;
  defaultAgCommunicationStrategy: AgCommunicationStrategy | undefined;
  sciMailWindows: SciMailWindowsMap;
  defaultDpeGeneralMailTemplate: DpeGeneralMailTemplateKey | undefined;
}

const DPE_GENERAL_MAIL_TEMPLATE_META: Record<DpeGeneralMailTemplateKey, { label: string; description: string }> = {
  v1: { label: DPE_GENERAL_MAIL_TEMPLATES.v1.label, description: DPE_GENERAL_MAIL_TEMPLATES.v1.description },
  v2: { label: DPE_GENERAL_MAIL_TEMPLATES.v2.label, description: DPE_GENERAL_MAIL_TEMPLATES.v2.description },
  v3: { label: DPE_GENERAL_MAIL_TEMPLATES.v3.label, description: DPE_GENERAL_MAIL_TEMPLATES.v3.description },
  v4: { label: DPE_GENERAL_MAIL_TEMPLATES.v4.label, description: DPE_GENERAL_MAIL_TEMPLATES.v4.description },
  'transaction-v1': {
    label: DPE_GENERAL_MAIL_TEMPLATES['transaction-v1'].label,
    description: DPE_GENERAL_MAIL_TEMPLATES['transaction-v1'].description,
  },
  'transaction-v2': {
    label: DPE_GENERAL_MAIL_TEMPLATES['transaction-v2'].label,
    description: DPE_GENERAL_MAIL_TEMPLATES['transaction-v2'].description,
  },
};

const normalizeStoredReferentCampaignKey = (
  key: ReferentCampaignTemplateKey | undefined,
): ReferentCampaignTemplateKey | undefined => {
  if (!key) { return undefined; }
  if (key === 'model4-custom') { return CUSTOM_TEMPLATE_KEY; }
  return key;
};

const buildInitialDraft = (company: Company | null): ContentDraft => ({
  defaultDpeFirstContactTemplate: company?.brandingSettings?.defaultDpeFirstContactTemplate,
  referentCampaigns: {
    1: normalizeStoredReferentCampaignKey(company?.brandingSettings?.defaultReferentCampaign1),
    2: normalizeStoredReferentCampaignKey(company?.brandingSettings?.defaultReferentCampaign2),
    3: normalizeStoredReferentCampaignKey(company?.brandingSettings?.defaultReferentCampaign3),
  },
  referentCampaignEmailText: company?.brandingSettings?.referentCampaignEmailText ?? DEFAULT_REFERENT_CAMPAIGN_EMAIL_TEXT,
  defaultAgCommunicationStrategy: company?.brandingSettings?.defaultAgCommunicationStrategy,
  sciMailWindows: company?.brandingSettings?.sciMailWindows ?? {},
  defaultDpeGeneralMailTemplate: company?.brandingSettings?.defaultDpeGeneralMailTemplate,
});

const referentCampaignsEqual = (a: ReferentCampaignDraft, b: ReferentCampaignDraft): boolean =>
  a[1] === b[1] && a[2] === b[2] && a[3] === b[3];

const sciMailWindowsEqual = (a: SciMailWindowsMap, b: SciMailWindowsMap): boolean => {
  const aKeys = Object.keys(a).sort();
  const bKeys = Object.keys(b).sort();
  if (aKeys.length !== bKeys.length) {return false;}
  for (let i = 0; i < aKeys.length; i++) {
    if (aKeys[i] !== bKeys[i]) {return false;}
    const ka = aKeys[i];
    if (a[ka].kind !== b[ka].kind || a[ka].templateKey !== b[ka].templateKey) {return false;}
  }
  return true;
};

type SectionAxe = 'axe1' | 'axe2';

const SECTION_AXE_COLORS: Record<SectionAxe, string> = {
  axe1: 'bg-green-600',
  axe2: 'bg-violet-600',
};

interface SectionShellProps {
  number: number;
  axe: SectionAxe;
  title: string;
  description: string;
  children: React.ReactNode;
}

function SectionShell({ number, axe, title, description, children }: SectionShellProps) {
  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <header className="mb-4 flex items-start gap-3">
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white ${SECTION_AXE_COLORS[axe]}`}>
          {number}
        </span>
        <div className="flex-1">
          <h3 className="text-lg font-bold text-slate-800">{title}</h3>
          <p className="mt-1 text-sm leading-relaxed text-gray-500">{description}</p>
        </div>
      </header>
      {children}
    </section>
  );
}

interface TemplateCardProps {
  selected: boolean;
  onSelect: () => void;
  onPreview?: () => void;
  icon: React.ElementType;
  label: string;
  description: string;
}

function TemplateCard({ selected, onSelect, onPreview, icon: Icon, label, description }: TemplateCardProps) {
  return (
    <div
      className={`group relative flex flex-col rounded-xl border-2 p-4 transition-all ${
        selected
          ? 'border-blue-500 bg-blue-50 shadow-sm'
          : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/40'
      }`}
    >
      {selected && (
        <span className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-blue-500 shadow">
          <CheckCircleIcon className="h-4 w-4 text-white" strokeWidth={2.5} />
        </span>
      )}
      <button
        type="button"
        onClick={onSelect}
        className="flex flex-1 flex-col items-start text-left"
      >
        <Icon className={`mb-2 h-7 w-7 ${selected ? 'text-blue-600' : 'text-gray-400 group-hover:text-blue-400'}`} />
        <span className={`text-sm font-semibold ${selected ? 'text-blue-700' : 'text-gray-800'}`}>{label}</span>
        <span className={`mt-1 text-xs leading-relaxed ${selected ? 'text-blue-600/80' : 'text-gray-500'}`}>{description}</span>
      </button>
      {onPreview && (
        <button
          type="button"
          onClick={onPreview}
          className="mt-3 inline-flex items-center justify-center gap-1.5 self-start rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-50"
        >
          <EyeIcon className="h-3.5 w-3.5" />
          Voir un exemple
        </button>
      )}
    </div>
  );
}

interface ReferentPickerProps {
  campaignNumber: ReferentCampaignIndex;
  headerLabel: string;
  value: ReferentCampaignTemplateKey | undefined;
  onChange: (value: ReferentCampaignTemplateKey | undefined) => void;
  onPreview: (modelId: ModelId, versionId: VersionId) => void;
  onDownload: (modelId: ModelId, versionId: VersionId) => void;
  downloadingKey: string | null;
  isDownloadDisabled: boolean;
  validReferentCount: number;
  customDoc: CustomDocFile | null;
  isUploadingCustomDoc: boolean;
  onUploadCustomDoc: (file: File) => void;
  onDeleteCustomDoc: () => void;
}

function ReferentPicker({
  campaignNumber,
  headerLabel,
  value,
  onChange,
  onPreview,
  onDownload,
  downloadingKey,
  isDownloadDisabled,
  validReferentCount,
  customDoc,
  isUploadingCustomDoc,
  onUploadCustomDoc,
  onDeleteCustomDoc,
}: ReferentPickerProps) {
  const parsed = parseTemplateKey(value);
  const customFileInputRef = useRef<HTMLInputElement | null>(null);

  const handleCustomFilePick = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onUploadCustomDoc(file);
    }
    event.target.value = '';
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-5">
      <div className="mb-4 flex items-baseline justify-between">
        <div className="flex items-center gap-2">
          <CalendarDaysIcon className="h-5 w-5 text-rgpd-blue" />
          <h4 className="text-base font-bold text-slate-800">{headerLabel}</h4>
        </div>
        {value ? (
          <button
            type="button"
            onClick={() => onChange(undefined)}
            className="text-xs font-medium text-gray-500 hover:text-gray-700 hover:underline"
          >
            Effacer le choix
          </button>
        ) : (
          <span className="text-xs text-gray-400">Aucun modèle sélectionné</span>
        )}
      </div>
      <div className="space-y-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 2xl:grid-cols-4">
          {BUILTIN_MODEL_IDS.map((modelId) => {
            const tab = tabsConfig.find(t => t.id === modelId);
            if (!tab) {return null;}

            return (
              <div key={modelId} className="relative rounded-lg border border-gray-200 bg-white p-3">
                {tab.recommended && (
                  <span className="absolute -top-2 -right-2 z-10 rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-white uppercase shadow">
                    Recommandé
                  </span>
                )}
                <div className="mb-2 flex items-center gap-2">
                  <span className="shrink-0">{tab.emoji}</span>
                  <div className="min-w-0 flex-1 leading-tight">
                    <div className="text-[10px] font-semibold tracking-wide text-gray-400 uppercase">{tab.badge}</div>
                    <div className="text-sm font-semibold break-words text-slate-800">{tab.title}</div>
                  </div>
                </div>
                <div className="flex flex-col gap-1.5">
                  {VERSION_ORDER.map((versionId) => {
                    const meta = versionLabels[modelId]?.[versionId];
                    if (!meta) { return null; }
                    const optionKey = buildTemplateKey(modelId, versionId);
                    const isSelected = parsed?.modelId === modelId && parsed?.versionId === versionId;
                    const downloadKey = `${modelId}-${versionId}`;
                    const isDownloading = downloadingKey === downloadKey;
                    const downloadTitle = validReferentCount === 0
                      ? 'Renseignez au moins un référent valide pour activer le téléchargement.'
                      : validReferentCount > 1
                        ? `Télécharger le ZIP (${validReferentCount} PDFs)`
                        : 'Télécharger en PDF';

                    return (
                      <div
                        key={versionId}
                        className={`flex items-center justify-between gap-2 rounded-md border px-2.5 py-1.5 text-xs transition-colors ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 bg-white hover:border-blue-300'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => onChange(optionKey)}
                          className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
                        >
                          <span className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border-2 ${
                            isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300 bg-white'
                          }`}>
                            {isSelected && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                          </span>
                          <span className={`min-w-0 font-medium break-words ${isSelected ? 'text-blue-700' : 'text-slate-700'}`}>
                            {meta.label}
                          </span>
                        </button>
                        <div className="flex shrink-0 items-center gap-0.5">
                          <button
                            type="button"
                            onClick={() => onPreview(modelId, versionId)}
                            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                            title="Voir un exemple"
                            aria-label={`Voir un exemple de ${tab.title} - ${meta.label}`}
                          >
                            <EyeIcon className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => onDownload(modelId, versionId)}
                            disabled={isDownloadDisabled || isDownloading}
                            className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-rgpd-blue disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-gray-400"
                            title={downloadTitle}
                            aria-label={`${downloadTitle} - ${tab.title} - ${meta.label}`}
                          >
                            <ArrowDownTrayIcon className={`h-3.5 w-3.5 ${isDownloading ? 'animate-pulse text-rgpd-blue' : ''}`} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {(() => {
          const tab = tabsConfig.find(t => t.id === CUSTOM_MODEL_ID);
          if (!tab) { return null; }
          const customMeta = versionLabels.model5?.custom;
          const customOptionKey = CUSTOM_TEMPLATE_KEY;
          const isCustomSelected = value === customOptionKey
            || (parsed?.modelId === CUSTOM_MODEL_ID && parsed.versionId === CUSTOM_VERSION_ID);
          const customDownloadKey = `${CUSTOM_MODEL_ID}-${CUSTOM_VERSION_ID}`;
          const isCustomDownloading = downloadingKey === customDownloadKey;

          return (
            <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="shrink-0">{tab.emoji}</span>
                <div className="min-w-0 flex-1 leading-tight">
                  <div className="text-[10px] font-semibold tracking-wide text-gray-400 uppercase">{tab.badge}</div>
                  <div className="text-sm font-semibold break-words text-slate-800">{tab.title}</div>
                </div>
              </div>
              <input
                ref={customFileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                className="hidden"
                onChange={handleCustomFilePick}
                aria-label={`Importer le document personnalisé pour la campagne ${campaignNumber}`}
              />
              {customDoc ? (
                <div
                  className={`flex flex-col gap-2 rounded-md border px-3 py-2.5 text-xs transition-colors sm:flex-row sm:items-center sm:justify-between ${
                    isCustomSelected
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 bg-white hover:border-blue-300'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => onChange(isCustomSelected ? undefined : customOptionKey)}
                    className="flex min-w-0 items-center gap-2 text-left"
                  >
                    <span className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border-2 ${
                      isCustomSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300 bg-white'
                    }`}>
                      {isCustomSelected && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                    </span>
                    <span className="min-w-0">
                      <span className={`block font-medium ${isCustomSelected ? 'text-blue-700' : 'text-slate-700'}`}>
                        {customMeta?.label ?? 'Document personnalisé'}
                      </span>
                      <span className="mt-0.5 block text-[11px] break-all text-slate-500" title={customDoc.filename}>
                        <span role="img" aria-label="pièce jointe" className="mr-1 align-middle">📎</span>
                        {customDoc.filename}
                      </span>
                    </span>
                  </button>
                  <div className="flex shrink-0 items-center gap-1 self-end sm:self-auto">
                    <button
                      type="button"
                      onClick={() => customFileInputRef.current?.click()}
                      disabled={isUploadingCustomDoc}
                      className="rounded border border-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-600 hover:border-rgpd-blue hover:text-rgpd-blue disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isUploadingCustomDoc ? 'Import…' : 'Remplacer'}
                    </button>
                    <button
                      type="button"
                      onClick={onDeleteCustomDoc}
                      disabled={isUploadingCustomDoc}
                      className="rounded border border-transparent px-2 py-0.5 text-[11px] font-semibold text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Supprimer
                    </button>
                    <button
                      type="button"
                      onClick={() => onDownload(CUSTOM_MODEL_ID, CUSTOM_VERSION_ID)}
                      disabled={isCustomDownloading}
                      className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-rgpd-blue disabled:cursor-not-allowed disabled:opacity-50"
                      title="Télécharger le document personnalisé"
                      aria-label={`Télécharger le document personnalisé pour la campagne ${campaignNumber}`}
                    >
                      <ArrowDownTrayIcon className={`h-3.5 w-3.5 ${isCustomDownloading ? 'animate-pulse text-rgpd-blue' : ''}`} />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-2 rounded-md border border-dashed border-gray-300 bg-white px-3 py-2.5 text-xs text-slate-600 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-[11px] leading-snug text-slate-500">
                    Importez votre propre document (PDF, DOC ou DOCX, max 10 Mo) pour cette campagne.
                  </p>
                  <button
                    type="button"
                    onClick={() => customFileInputRef.current?.click()}
                    disabled={isUploadingCustomDoc}
                    className="self-start rounded border border-rgpd-blue px-2 py-1 text-[11px] font-semibold text-rgpd-blue hover:bg-rgpd-blue hover:text-white disabled:cursor-not-allowed disabled:opacity-60 sm:self-auto"
                  >
                    {isUploadingCustomDoc ? 'Import en cours…' : 'Importer un document'}
                  </button>
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

interface SciMailWindowPickerProps {
  window: SciMailWindow;
  selectedKind: SciMailKind | null;
  selectedTemplateKey: string | null;
  onChange: (kind: SciMailKind | null, templateKey: string | null) => void;
  onPreviewRent: (id: number) => void;
  onPreviewTransaction: (id: number) => void;
}

function SciMailWindowPicker({
  window,
  selectedKind,
  selectedTemplateKey,
  onChange,
  onPreviewRent,
  onPreviewTransaction,
}: SciMailWindowPickerProps) {
  // The kind toggle is purely a UI filter for the template grid; only the
  // (kind, templateKey) pair is persisted in the parent draft. Track the kind
  // locally so clicking it sticks even before a template is picked. We only
  // pull the persisted kind down when it becomes non-null (initial load /
  // external save) — never let the effect overwrite an in-progress local
  // choice back to null, which would create a feedback loop with our own
  // `onChange(null, null)` calls.
  const [activeKind, setActiveKind] = useState<SciMailKind | null>(selectedKind);
  useEffect(() => {
    if (selectedKind !== null) {
      setActiveKind(selectedKind);
    }
  }, [selectedKind]);

  const plannedYear = new Date(`${window.plannedDate}T00:00:00`).getFullYear();
  const headerLabel = `SCI ${window.sciIndex} — ${window.plannedLabel} ${plannedYear}`;
  // Show the persisted template id only when the active kind matches the
  // persisted kind — switching kind clears the visible template selection.
  const selectedId = activeKind === selectedKind ? parseSciKeyId(selectedTemplateKey) : null;

  const templates = activeKind === 'rent' ? rentMailTemplates : activeKind === 'transaction' ? transactionMailTemplates : null;

  const handleSelectTemplate = (id: number) => {
    if (!activeKind) {return;}
    const key = activeKind === 'rent' ? buildSciRentKey(id) : buildSciTransactionKey(id);
    onChange(activeKind, key);
  };

  const handleSelectKind = (kind: SciMailKind) => {
    if (kind === activeKind) {return;}
    setActiveKind(kind);
    if (kind !== selectedKind) {
      onChange(null, null);
    }
  };

  const handleClear = () => {
    setActiveKind(null);
    onChange(null, null);
  };

  const handlePreview = (id: number) => {
    if (activeKind === 'rent') {onPreviewRent(id);}
    if (activeKind === 'transaction') {onPreviewTransaction(id);}
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-5">
      <header className="mb-4 flex items-baseline justify-between">
        <div className="flex items-center gap-2">
          <CalendarDaysIcon className="h-5 w-5 text-rgpd-blue" />
          <h4 className="text-base font-bold text-slate-800">{headerLabel}</h4>
        </div>
        {(activeKind || selectedKind) && (
          <button
            type="button"
            onClick={handleClear}
            className="text-xs font-medium text-gray-500 hover:text-gray-700 hover:underline"
          >
            Effacer le choix
          </button>
        )}
      </header>

      <div className="mb-4 flex flex-wrap gap-2" role="radiogroup" aria-label={`Type de mail SCI pour ${headerLabel}`}>
        {(['rent', 'transaction'] as const).map((kind) => {
          const isActive = activeKind === kind;
          const label = kind === 'rent' ? 'Gestion locative' : 'Transaction';
          return (
            <button
              key={kind}
              type="button"
              role="radio"
              aria-checked={isActive}
              onClick={() => handleSelectKind(kind)}
              className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${
                isActive ? 'border-rgpd-blue bg-rgpd-blue text-white shadow-sm' : 'border-gray-300 bg-white text-gray-600 hover:border-rgpd-blue hover:text-rgpd-blue'
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      {!templates && (
        <p className="text-xs text-gray-500">Choisissez un type de mail puis le modèle à utiliser pour cette date.</p>
      )}

      {templates && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {templates.map((template) => {
            const isSelected = selectedId === template.id;
            const handleCardKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleSelectTemplate(template.id);
              }
            };
            return (
              <div
                key={template.id}
                role="radio"
                tabIndex={0}
                aria-checked={isSelected}
                onClick={() => handleSelectTemplate(template.id)}
                onKeyDown={handleCardKeyDown}
                className={`group relative flex cursor-pointer flex-col rounded-xl border-2 p-4 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50 shadow-sm'
                    : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/40'
                }`}
              >
                {isSelected && (
                  <span className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-blue-500 shadow">
                    <CheckCircleIcon className="h-4 w-4 text-white" strokeWidth={2.5} />
                  </span>
                )}
                <div className="flex flex-1 flex-col items-start text-left">
                  <span className={`text-xs font-semibold tracking-wide uppercase ${isSelected ? 'text-blue-600' : 'text-gray-400'}`}>
                    Modèle {template.id}
                  </span>
                  <span className={`mt-1 text-sm font-semibold ${isSelected ? 'text-blue-700' : 'text-gray-800'}`}>{template.titre}</span>
                  <span className={`mt-1 text-xs leading-relaxed ${isSelected ? 'text-blue-600/80' : 'text-gray-500'}`}>{template.apercu}</span>
                </div>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    handlePreview(template.id);
                  }}
                  className="relative z-10 mt-3 inline-flex items-center justify-center gap-1.5 self-start rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-50"
                >
                  <EyeIcon className="h-3.5 w-3.5" />
                  Voir un exemple
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export interface ContentChoiceHandle {
  saveIfDirty: () => Promise<boolean>;
}

const ContentChoice = forwardRef<ContentChoiceHandle>((_props, ref) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const initial = useMemo(() => buildInitialDraft(company ?? null), [company]);
  const [draft, setDraft] = useState<ContentDraft>(initial);
  const [isSaving, setIsSaving] = useState(false);
  const [previewTarget, setPreviewTarget] = useState<PreviewTarget | null>(null);
  const [sciMailWindows, setSciMailWindows] = useState<SciMailWindow[] | null>(null);
  const [isSciWindowsLoading, setIsSciWindowsLoading] = useState<boolean>(true);
  const [referentCampaignWindows, setReferentCampaignWindows] = useState<ReferentCampaignWindow[] | null>(null);
  const [isReferentWindowsLoading, setIsReferentWindowsLoading] = useState<boolean>(true);
  const [downloadingReferentKey, setDownloadingReferentKey] = useState<string | null>(null);

  const validReferents = useMemo(() => getValidReferents(company), [company]);

  useEffect(() => {
    const run = async() => {
      if (!company?.id || !jwt) { setIsSciWindowsLoading(false); return; }
      setIsSciWindowsLoading(true);
      try {
        const resp = await fetch(getCompanySciMailWindows(company.id), JWTHeader(jwt));
        if (!resp.ok) { throw new Error(`${resp.status}`); }
        const data: SciMailWindow[] = await resp.json();
        setSciMailWindows(data);
      } catch (e) {
        console.error('ContentChoice - failed to fetch SCI mail windows:', e);
        setSciMailWindows([]);
      } finally {
        setIsSciWindowsLoading(false);
      }
    };
    run();
  }, [company?.id, jwt]);

  useEffect(() => {
    const run = async() => {
      if (!company?.id || !jwt) { setIsReferentWindowsLoading(false); return; }
      setIsReferentWindowsLoading(true);
      try {
        const resp = await fetch(getCompanyReferentCampaignWindows(company.id), JWTHeader(jwt));
        if (!resp.ok) { throw new Error(`${resp.status}`); }
        const data: ReferentCampaignWindow[] = await resp.json();
        setReferentCampaignWindows(data);
      } catch (e) {
        console.error('ContentChoice - failed to fetch Référent campaign windows:', e);
        setReferentCampaignWindows([]);
      } finally {
        setIsReferentWindowsLoading(false);
      }
    };
    run();
  }, [company?.id, jwt]);

  const isDirty = useMemo(() => (
    draft.defaultDpeFirstContactTemplate !== initial.defaultDpeFirstContactTemplate
    || !referentCampaignsEqual(draft.referentCampaigns, initial.referentCampaigns)
    || draft.referentCampaignEmailText !== initial.referentCampaignEmailText
    || draft.defaultAgCommunicationStrategy !== initial.defaultAgCommunicationStrategy
    || !sciMailWindowsEqual(draft.sciMailWindows, initial.sciMailWindows)
    || draft.defaultDpeGeneralMailTemplate !== initial.defaultDpeGeneralMailTemplate
  ), [draft, initial]);

  // Mirror `isDirty` into a ref so the navigation blocker reads the live value
  // synchronously — needed when we save then immediately navigate, since the
  // component has not yet re-rendered with the new `isDirty = false` value.
  const isDirtyRef = useRef(isDirty);
  useEffect(() => { isDirtyRef.current = isDirty; }, [isDirty]);

  // Browser-level guard: prompts the native confirmation when the user closes
  // the tab, reloads, or navigates to an external URL with pending edits.
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isDirtyRef.current) { return; }
      e.preventDefault();
      e.returnValue = '';
    };
    if (isDirty) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }
    return () => {};
  }, [isDirty]);

  // In-app guard: catches every router navigation (sidebar links, back button,
  // and the Parametrage tab switcher which is now driven by `setSearchParams`).
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (!isDirtyRef.current) { return false; }
    return (
      currentLocation.pathname !== nextLocation.pathname
      || currentLocation.search !== nextLocation.search
    );
  });

  // Mirror the planning module's "row toggles" so the corresponding sections
  // disappear from the Contenu tab when the user has turned an entire row off.
  // The visibility map is stored per year inside `toolsTimeline`; we use the
  // current calendar year as the reference, falling back to nearest configured
  // years (see {@link resolveTimelineRowVisibility}).
  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const isReferentRowVisible = useMemo(
    () => resolveTimelineRowVisibility(company, currentYear, 'referent-mails'),
    [company, currentYear],
  );
  const isAgRowVisible = useMemo(
    () => resolveTimelineRowVisibility(company, currentYear, 'ag-communication'),
    [company, currentYear],
  );
  const isSciRowVisible = useMemo(
    () => resolveTimelineRowVisibility(company, currentYear, 'sci-mails'),
    [company, currentYear],
  );
  const isBoitageRowVisible = useMemo(
    () => resolveTimelineRowVisibility(company, currentYear, 'boitage'),
    [company, currentYear],
  );

  // The AG "réutilise" hint inherits the template from the first active Référent
  // campaign — typically Campaign 1, but we fall back to the lowest-index
  // campaign that is currently planned in case the user has only activated 2/3.
  const inheritedAgCampaignIndex: ReferentCampaignIndex = useMemo(() => (
    referentCampaignWindows && referentCampaignWindows.length > 0
      ? referentCampaignWindows[0].campaignIndex
      : 1
  ), [referentCampaignWindows]);
  const inheritedAgReferent = useMemo(
    () => parseTemplateKey(draft.referentCampaigns[inheritedAgCampaignIndex]),
    [draft.referentCampaigns, inheritedAgCampaignIndex],
  );

  const handlePreviewFirstContact = (templateType: FirstContactTemplateType) => {
    setPreviewTarget({ kind: 'dpe-first-contact', templateType });
  };

  const handlePreviewGeneralMail = (templateKey: DpeGeneralMailTemplateKey) => {
    setPreviewTarget({ kind: 'dpe-general-mailing', templateKey });
  };

  const handlePreviewReferent = (modelId: ModelId, versionId: VersionId) => {
    setPreviewTarget({ kind: 'referent', modelId, versionId });
  };

  const handleDownloadReferent = async(modelId: ModelId, versionId: VersionId, campaignNumber?: ReferentCampaignIndex) => {
    if (!company) { return; }
    const key = `${modelId}-${versionId}`;
    setDownloadingReferentKey(key);
    try {
      if (isCustomModelId(modelId)) {
        if (!campaignNumber) {
          showToast('error', 'Campagne introuvable pour le téléchargement du document personnalisé.');
          return;
        }
        const customDoc = getCustomDocForCampaign(company.brandingSettings?.referentCampaignCustomDocs, campaignNumber);
        if (!customDoc) {
          showToast('error', 'Aucun document personnalisé importé pour cette campagne.');
          return;
        }
        await downloadCustomDoc(company.id, campaignNumber, jwt, customDoc.filename);
        showToast('success', 'Document téléchargé.');
        return;
      }

      if (validReferents.length === 0) {
        showToast('error', 'Aucun référent valide configuré. Renseignez au moins un référent pour générer le PDF.');
        return;
      }
      await downloadReferentCampaignAsPDFOrZip({
        modelId,
        versionId,
        referents: validReferents,
        company,
      });
      if (validReferents.length > 1) {
        showToast('success', `ZIP téléchargé (${validReferents.length} PDFs).`);
      } else {
        showToast('success', 'PDF téléchargé.');
      }
    } catch (error) {
      console.error('Referent campaign PDF download failed:', error);
      showToast('error', 'Une erreur est survenue lors du téléchargement.');
    } finally {
      setDownloadingReferentKey(null);
    }
  };

  const [uploadingCustomDocCampaign, setUploadingCustomDocCampaign] = useState<ReferentCampaignIndex | null>(null);

  const handleUploadCustomDoc = useCallback(async(campaign: ReferentCampaignIndex, file: File) => {
    if (!company?.id) { return; }
    const validationError = validateCustomDocFile(file);
    if (validationError) {
      showToast('error', validationError);
      return;
    }
    setUploadingCustomDocCampaign(campaign);
    try {
      const updated = await uploadCustomDoc(company.id, campaign, file, jwt);
      dispatch(refreshCompanyData(updated));
      setDraft(prev => ({
        ...prev,
        referentCampaigns: { ...prev.referentCampaigns, [campaign]: CUSTOM_TEMPLATE_KEY },
      }));
      showToast('success', 'Document personnalisé importé et sélectionné comme Modèle 5.');
    } catch (error) {
      console.error('Custom referent campaign doc upload failed:', error);
      showToast('error', error instanceof Error ? error.message : "Échec de l'import du document.");
    } finally {
      setUploadingCustomDocCampaign(null);
    }
  }, [company?.id, dispatch, jwt]);

  const handleDeleteCustomDoc = useCallback(async(campaign: ReferentCampaignIndex) => {
    if (!company?.id) { return; }
    setUploadingCustomDocCampaign(campaign);
    try {
      const updated = await deleteCustomDoc(company.id, campaign, jwt);
      // If the deleted doc was used as the default Modèle 5 for that campaign,
      // clear the selection in the local draft to match the server which also
      // clears it on delete.
      if (draft.referentCampaigns[campaign] === CUSTOM_TEMPLATE_KEY) {
        setDraft(prev => ({
          ...prev,
          referentCampaigns: { ...prev.referentCampaigns, [campaign]: undefined },
        }));
      }
      dispatch(refreshCompanyData(updated));
      showToast('success', 'Document personnalisé supprimé.');
    } catch (error) {
      console.error('Custom referent campaign doc delete failed:', error);
      showToast('error', error instanceof Error ? error.message : 'Échec de la suppression du document.');
    } finally {
      setUploadingCustomDocCampaign(null);
    }
  }, [company?.id, dispatch, jwt, draft.referentCampaigns]);

  const handlePreviewAgReferent = () => {
    if (!inheritedAgReferent) {return;}
    setPreviewTarget({ kind: 'ag-referent', modelId: inheritedAgReferent.modelId, versionId: inheritedAgReferent.versionId });
  };

  const handlePreviewSciRent = (id: number) => {
    const template = rentMailTemplates.find(t => t.id === id);
    if (!template) {return;}
    setPreviewTarget({ kind: 'sci', line: 'rent', id: template.id, titre: template.titre, objet: template.objet, content: template.content });
  };

  const handlePreviewSciTransaction = (id: number) => {
    const template = transactionMailTemplates.find(t => t.id === id);
    if (!template) {return;}
    setPreviewTarget({ kind: 'sci', line: 'transaction', id: template.id, titre: template.titre, objet: template.objet, content: template.content });
  };

  const handleSave = async(): Promise<boolean> => {
    if (!company?.id) {return false;}
    setIsSaving(true);
    try {
      const payload = {
        defaultDpeFirstContactTemplate: draft.defaultDpeFirstContactTemplate,
        defaultReferentCampaign1: draft.referentCampaigns[1],
        defaultReferentCampaign2: draft.referentCampaigns[2],
        defaultReferentCampaign3: draft.referentCampaigns[3],
        referentCampaignEmailText: draft.referentCampaignEmailText,
        defaultAgCommunicationStrategy: draft.defaultAgCommunicationStrategy,
        sciMailWindows: draft.sciMailWindows,
        defaultDpeGeneralMailTemplate: draft.defaultDpeGeneralMailTemplate,
      };

      const response = await fetch(`/api/companies/${company.id}/branding-settings`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: JWTAuthorization(jwt),
        },
        body: JSON.stringify({ brandingSettings: payload }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error((errorData && (errorData.error || errorData.message)) || 'Échec de la sauvegarde');
      }

      dispatch(updateCompanyBranding(payload));
      // Clear the dirty flag synchronously: the redux dispatch above will
      // update `isDirty` on the next render, but in the meantime any caller
      // that immediately navigates (e.g. "save and go to boitage") would
      // otherwise be intercepted by the blocker we register below.
      isDirtyRef.current = false;
      showToast('success', 'Préférences de contenu enregistrées.');
      return true;
    } catch (error) {
      console.error('ContentChoice - failed to save preferences:', error);
      showToast('error', "Impossible d'enregistrer vos préférences. Veuillez réessayer.");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndGoToBoitageQualification = async() => {
    if (isDirty) {
      const ok = await handleSave();
      if (!ok) {return;}
    }
    navigate(ROUTES.PROSPECTING_BOITAGE_QUALIFICATION);
  };

  useImperativeHandle(ref, () => ({
    saveIfDirty: async() => {
      if (!isDirty) {return true;}
      return handleSave();
    },
  }), [isDirty, handleSave]);

  const handleReset = () => {
    setDraft(initial);
  };

  const handleBlockerStay = () => {
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  };

  const handleBlockerLeaveWithoutSaving = () => {
    if (blocker.state !== 'blocked') { return; }
    isDirtyRef.current = false;
    blocker.proceed();
  };

  const handleBlockerSaveAndLeave = async() => {
    if (blocker.state !== 'blocked') { return; }
    const ok = await handleSave();
    if (!ok) { return; }
    blocker.proceed();
  };

  const handleSciWindowChange = (year: number, sciIndex: 1 | 2 | 3, kind: SciMailKind | null, templateKey: string | null) => {
    const key = buildSciMailWindowKey(year, sciIndex);
    setDraft((prev) => {
      const existing = prev.sciMailWindows[key];
      if (kind && templateKey) {
        if (existing && existing.kind === kind && existing.templateKey === templateKey) {
          return prev;
        }
        return { ...prev, sciMailWindows: { ...prev.sciMailWindows, [key]: { kind, templateKey } } };
      }
      if (!existing) {
        return prev;
      }
      const next: SciMailWindowsMap = { ...prev.sciMailWindows };
      delete next[key];
      return { ...prev, sciMailWindows: next };
    });
  };

  const newsletterTemplate = company?.brandingSettings?.defaultTemplate;

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-blue-200 bg-blue-50 px-5 py-4 text-sm text-blue-800">
        Choisissez ici les modèles de communication par défaut. Ces choix pré-sélectionneront
        automatiquement les contenus dans vos suivis propriétaires (DPE), vos campagnes annuelles
        Référent et vos envois aux SCI. Vous pourrez toujours modifier le modèle au cas par cas
        avant chaque envoi.
      </div>

      <SectionShell
        number={1}
        axe="axe1"
        title="Newsletter — modèle en vigueur"
        description="Rappel du modèle de newsletter actuellement choisi pour votre cabinet. Pour le modifier, rendez-vous dans l'écran dédié."
      >
        <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-rgpd-blue/10 text-rgpd-blue">
              <NewspaperIcon className="h-5 w-5" />
            </span>
            <div>
              <div className="text-sm font-semibold text-slate-800">
                {newsletterTemplate ? `Modèle « ${newsletterTemplate} »` : 'Aucun modèle de newsletter sélectionné'}
              </div>
              <div className="text-xs text-gray-500">
                {newsletterTemplate
                  ? 'Ce modèle est utilisé pour générer vos newsletters mensuelles.'
                  : 'Choisissez un modèle pour activer la génération de newsletters personnalisées.'}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate(ROUTES.COMMUNICATION_NEWSLETTER_TEMPLATE_SELECTION)}
            className="inline-flex items-center justify-center gap-1.5 rounded-md border border-rgpd-blue px-3 py-1.5 text-xs font-semibold text-rgpd-blue transition-colors hover:bg-rgpd-blue hover:text-white"
          >
            <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
            {newsletterTemplate ? 'Modifier le modèle' : 'Configurer la newsletter'}
          </button>
        </div>
      </SectionShell>

      {isReferentRowVisible && (
      <SectionShell
        number={2}
        axe="axe1"
        title="Référent mutation location — campagnes annuelles"
        description="Pour chaque campagne annuelle planifiée pour le Référent Mutation Location, choisissez le modèle et la version pré-sélectionnés par défaut."
      >
        <div className="mb-5 rounded-xl border border-slate-200 bg-slate-50/60 p-5">
          <div className="mb-2 flex flex-col gap-1">
            <h4 className="text-base font-bold text-slate-800">Texte de l&apos;email</h4>
            <p className="text-xs text-slate-500">
              Texte personnalisé partagé entre toutes les campagnes Référent. Il est rappelé sur la page
              « Communication Référent Mutation Location » lors de l&apos;envoi et n&apos;est pas inclus dans le PDF généré.
            </p>
          </div>
          <textarea
            className="w-full rounded-lg border border-slate-300 p-3 px-4 text-sm text-slate-800 focus:border-rgpd-blue focus:ring-1 focus:ring-rgpd-blue focus:outline-none"
            rows={5}
            value={draft.referentCampaignEmailText}
            onChange={(event) => setDraft(prev => ({ ...prev, referentCampaignEmailText: event.target.value }))}
            placeholder="Saisissez ici le texte personnalisé à utiliser lors de l'envoi (objet, message d'accompagnement…)."
          />
        </div>

        {isReferentWindowsLoading && (
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-5 text-sm text-slate-500">
            Chargement des campagnes planifiées…
          </div>
        )}

        {!isReferentWindowsLoading && referentCampaignWindows && referentCampaignWindows.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-5 text-center">
            <p className="text-sm font-semibold text-slate-700">Aucune campagne Référent planifiée</p>
            <p className="mt-1 text-xs text-slate-500">
              Activez et positionnez vos campagnes Référent dans le planning pour pouvoir leur associer un modèle.
            </p>
            <button
              type="button"
              onClick={() => navigate(`${ROUTES.NEW_DASHBOARD_PARAMETRAGE}?tab=planning`)}
              className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-md border border-rgpd-blue px-3 py-1.5 text-xs font-semibold text-rgpd-blue transition-colors hover:bg-rgpd-blue hover:text-white"
            >
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
              Ouvrir le planning
            </button>
          </div>
        )}

        {!isReferentWindowsLoading && referentCampaignWindows && referentCampaignWindows.length > 0 && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {referentCampaignWindows.map((window) => {
              const plannedYear = new Date(`${window.plannedDate}T00:00:00`).getFullYear();
              const headerLabel = `Campagne ${window.campaignIndex} — ${window.plannedLabel} ${plannedYear}`;
              const customDoc = getCustomDocForCampaign(
                company?.brandingSettings?.referentCampaignCustomDocs,
                window.campaignIndex,
              );
              return (
                <ReferentPicker
                  key={`${window.year}:${window.campaignIndex}`}
                  campaignNumber={window.campaignIndex}
                  headerLabel={headerLabel}
                  value={draft.referentCampaigns[window.campaignIndex]}
                  onChange={(value) => setDraft(prev => ({
                    ...prev,
                    referentCampaigns: { ...prev.referentCampaigns, [window.campaignIndex]: value },
                  }))}
                  onPreview={handlePreviewReferent}
                  onDownload={(modelId, versionId) => { void handleDownloadReferent(modelId, versionId, window.campaignIndex); }}
                  downloadingKey={downloadingReferentKey}
                  isDownloadDisabled={validReferents.length === 0}
                  validReferentCount={validReferents.length}
                  customDoc={customDoc}
                  isUploadingCustomDoc={uploadingCustomDocCampaign === window.campaignIndex}
                  onUploadCustomDoc={(file) => { void handleUploadCustomDoc(window.campaignIndex, file); }}
                  onDeleteCustomDoc={() => { void handleDeleteCustomDoc(window.campaignIndex); }}
                />
              );
            })}
          </div>
        )}
      </SectionShell>
      )}

      {isAgRowVisible && (
      <SectionShell
        number={3}
        axe="axe1"
        title="Communication lors des AG"
        description="Choisissez la stratégie utilisée pendant les assemblées générales : réutiliser votre modèle de communication Référent ou éditer un rapport de copropriété."
      >
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <TemplateCard
            selected={draft.defaultAgCommunicationStrategy === 'referent-campaign'}
            onSelect={() => setDraft(prev => ({ ...prev, defaultAgCommunicationStrategy: 'referent-campaign' }))}
            onPreview={inheritedAgReferent ? handlePreviewAgReferent : undefined}
            icon={UserGroupIcon}
            label="Réutiliser le template communication au référent"
            description={`Repose sur le modèle choisi pour la Campagne ${inheritedAgCampaignIndex} ci-dessus afin d'assurer une cohérence d'image.`}
          />
          <TemplateCard
            selected={draft.defaultAgCommunicationStrategy === 'copro-report'}
            onSelect={() => setDraft(prev => ({ ...prev, defaultAgCommunicationStrategy: 'copro-report' }))}
            icon={DocumentTextIcon}
            label="Éditer un rapport de copropriété"
            description="Génère un rapport adapté à la copropriété concernée à présenter pendant l'AG."
          />
        </div>

        {draft.defaultAgCommunicationStrategy === 'referent-campaign' && (
          inheritedAgReferent ? (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
              <span aria-hidden="true"><span role="img" aria-label="emoji">★</span></span>
              <span>
                Réutilise: {tabsConfig.find(t => t.id === inheritedAgReferent.modelId)?.title ?? inheritedAgReferent.modelId}
                {' · '}
                {versionLabels[inheritedAgReferent.modelId]?.[inheritedAgReferent.versionId]?.label ?? ''}
              </span>
            </div>
          ) : (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
              <span aria-hidden="true">!</span>
              <span>Choisissez d'abord un modèle pour la Campagne {inheritedAgCampaignIndex} (section 2).</span>
            </div>
          )
        )}

        {draft.defaultAgCommunicationStrategy === 'copro-report' && (
          <div className="mt-4 flex flex-col gap-2 rounded-xl border border-slate-200 bg-slate-50/60 p-4 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-sm text-slate-600">
              Le rapport de copropriété se génère par copropriété, à partir de la fiche de chaque immeuble.
            </span>
            <button
              type="button"
              onClick={() => navigate(ROUTES.ANALYZING_GENERATE_COPRO_REPORT)}
              className="inline-flex items-center justify-center gap-1.5 rounded-md border border-rgpd-blue px-3 py-1.5 text-xs font-semibold text-rgpd-blue transition-colors hover:bg-rgpd-blue hover:text-white"
            >
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
              Ouvrir la génération de rapport
            </button>
          </div>
        )}

        {draft.defaultAgCommunicationStrategy && (
          <button
            type="button"
            onClick={() => setDraft(prev => ({ ...prev, defaultAgCommunicationStrategy: undefined }))}
            className="mt-3 text-xs font-medium text-gray-500 hover:text-gray-700 hover:underline"
          >
            Aucune stratégie par défaut
          </button>
        )}
      </SectionShell>
      )}

      <SectionShell
        number={4}
        axe="axe2"
        title="DPE — Premier contact"
        description="Modèle de mail proposé par défaut quand vous initiez le premier contact avec un propriétaire dans la liste des « Non contactés »."
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {FIRST_CONTACT_DPE_OPTIONS.map((option) => (
              <TemplateCard
                key={option}
                selected={draft.defaultDpeFirstContactTemplate === option}
                onSelect={() => setDraft(prev => ({ ...prev, defaultDpeFirstContactTemplate: option }))}
                onPreview={() => handlePreviewFirstContact(option)}
                icon={FIRST_CONTACT_ICONS[option]}
                label={FIRST_CONTACT_TEMPLATE_LABELS[option]}
                description={FIRST_CONTACT_TEMPLATE_DESCRIPTIONS[option]}
              />
            ))}
          </div>
          <div>
            <p className="mb-2 text-xs font-semibold tracking-wide text-slate-500 uppercase">
              Référent transaction sans notion des DPEs
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {FIRST_CONTACT_TRANSACTION_OPTIONS.map((option) => (
                <TemplateCard
                  key={option}
                  selected={draft.defaultDpeFirstContactTemplate === option}
                  onSelect={() => setDraft(prev => ({ ...prev, defaultDpeFirstContactTemplate: option }))}
                  onPreview={() => handlePreviewFirstContact(option)}
                  icon={FIRST_CONTACT_ICONS[option]}
                  label={FIRST_CONTACT_TEMPLATE_LABELS[option]}
                  description={FIRST_CONTACT_TEMPLATE_DESCRIPTIONS[option]}
                />
              ))}
            </div>
          </div>
        </div>
        {draft.defaultDpeFirstContactTemplate && (
          <button
            type="button"
            onClick={() => setDraft(prev => ({ ...prev, defaultDpeFirstContactTemplate: undefined }))}
            className="mt-3 text-xs font-medium text-gray-500 hover:text-gray-700 hover:underline"
          >
            Aucun modèle par défaut
          </button>
        )}
      </SectionShell>

      <SectionShell
        number={5}
        axe="axe2"
        title="Envoi général à tous les copropriétaires"
        description="Modèle d'email proposé par défaut quand vous déclenchez un envoi général à tous les copropriétaires d'un immeuble depuis la qualification d'un DPE."
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {GENERAL_MAIL_STANDARD_OPTIONS.map((option) => (
              <TemplateCard
                key={option}
                selected={draft.defaultDpeGeneralMailTemplate === option}
                onSelect={() => setDraft(prev => ({ ...prev, defaultDpeGeneralMailTemplate: option }))}
                onPreview={() => handlePreviewGeneralMail(option)}
                icon={NewspaperIcon}
                label={DPE_GENERAL_MAIL_TEMPLATE_META[option].label}
                description={DPE_GENERAL_MAIL_TEMPLATE_META[option].description}
              />
            ))}
          </div>
          <div>
            <p className="mb-2 text-xs font-semibold tracking-wide text-slate-500 uppercase">
              Référent transaction sans notion des DPEs
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {GENERAL_MAIL_TRANSACTION_OPTIONS.map((option) => (
                <TemplateCard
                  key={option}
                  selected={draft.defaultDpeGeneralMailTemplate === option}
                  onSelect={() => setDraft(prev => ({ ...prev, defaultDpeGeneralMailTemplate: option }))}
                  onPreview={() => handlePreviewGeneralMail(option)}
                  icon={GENERAL_MAIL_TRANSACTION_ICONS[option]}
                  label={DPE_GENERAL_MAIL_TEMPLATE_META[option].label}
                  description={DPE_GENERAL_MAIL_TEMPLATE_META[option].description}
                />
              ))}
            </div>
          </div>
        </div>
        {draft.defaultDpeGeneralMailTemplate && (
          <button
            type="button"
            onClick={() => setDraft(prev => ({ ...prev, defaultDpeGeneralMailTemplate: undefined }))}
            className="mt-3 text-xs font-medium text-gray-500 hover:text-gray-700 hover:underline"
          >
            Aucun modèle par défaut
          </button>
        )}
      </SectionShell>

      {isSciRowVisible && (
      <SectionShell
        number={6}
        axe="axe2"
        title="Envois de mails aux SCI"
        description="Pour chaque date d'envoi planifiée aux SCI, choisissez si vous envoyez un mail Gestion locative ou Transaction, puis le modèle correspondant."
      >
        {isSciWindowsLoading && (
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-5 text-sm text-slate-500">
            Chargement des dates planifiées…
          </div>
        )}

        {!isSciWindowsLoading && sciMailWindows && sciMailWindows.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-5 text-center">
            <p className="text-sm font-semibold text-slate-700">Aucune date d'envoi SCI planifiée</p>
            <p className="mt-1 text-xs text-slate-500">
              Activez et positionnez vos envois SCI dans le planning pour pouvoir y associer un modèle.
            </p>
            <button
              type="button"
              onClick={() => navigate(`${ROUTES.NEW_DASHBOARD_PARAMETRAGE}?tab=planning`)}
              className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-md border border-rgpd-blue px-3 py-1.5 text-xs font-semibold text-rgpd-blue transition-colors hover:bg-rgpd-blue hover:text-white"
            >
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
              Ouvrir le planning
            </button>
          </div>
        )}

        {!isSciWindowsLoading && sciMailWindows && sciMailWindows.length > 0 && (
          <div className="space-y-4">
            {sciMailWindows.map((window) => {
              const key = buildSciMailWindowKey(window.year, window.sciIndex);
              const entry = draft.sciMailWindows[key];
              return (
                <SciMailWindowPicker
                  key={key}
                  window={window}
                  selectedKind={entry?.kind ?? null}
                  selectedTemplateKey={entry?.templateKey ?? null}
                  onChange={(kind, templateKey) => handleSciWindowChange(window.year, window.sciIndex, kind, templateKey)}
                  onPreviewRent={handlePreviewSciRent}
                  onPreviewTransaction={handlePreviewSciTransaction}
                />
              );
            })}
          </div>
        )}
      </SectionShell>
      )}

      {isBoitageRowVisible && (
      <SectionShell
        number={7}
        axe="axe2"
        title="Boitage — modèles V1/V2/V3 et stop pub"
        description="Téléchargez les modèles types ou déposez vos propres versions des documents stop pub et sans stop pub."
      >
        <div className="-mx-1">
          <BoitageDocumentsPanel />
        </div>
        <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
          <HomeModernIcon className="h-3.5 w-3.5" />
          <span>Les documents enregistrés ici sont automatiquement utilisés sur la page Boitage Transaction.</span>
        </div>
        <div className="mt-6 overflow-hidden rounded-2xl border-2 border-rgpd-blue/30 bg-gradient-to-br from-blue-50 via-white to-blue-50/40 shadow-sm">
          <div className="flex items-center gap-2 border-b border-rgpd-blue/20 bg-rgpd-blue/5 px-5 py-2.5">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-rgpd-blue text-white">
              <ArrowRightIcon className="h-3.5 w-3.5" />
            </span>
            <span className="text-xs font-bold tracking-wider text-rgpd-blue uppercase">
              Prochaine étape
            </span>
          </div>

          <div className="flex flex-col gap-5 p-5">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-rgpd-blue/10 text-rgpd-blue">
                <BuildingOffice2Icon className="h-5 w-5" />
              </span>
              <div>
                <h4 className="text-base font-bold text-slate-800">
                  Choisissez les copropriétés ciblées par le boitage
                </h4>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">
                  Une fois vos modèles déposés, passez au choix des copropriétés ciblées par le boitage.
                </p>
              </div>
            </div>

            <ul className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <li className="flex items-center gap-2 rounded-lg bg-white/70 px-3 py-2 text-xs text-slate-600 ring-1 ring-rgpd-blue/10">
                <CheckCircleIcon className="h-4 w-4 shrink-0 text-rgpd-blue" />
                <span>Identifier les immeubles à fort potentiel</span>
              </li>
              <li className="flex items-center gap-2 rounded-lg bg-white/70 px-3 py-2 text-xs text-slate-600 ring-1 ring-rgpd-blue/10">
                <CheckCircleIcon className="h-4 w-4 shrink-0 text-rgpd-blue" />
                <span>Sélectionner les copropriétés à inclure</span>
              </li>
              <li className="flex items-center gap-2 rounded-lg bg-white/70 px-3 py-2 text-xs text-slate-600 ring-1 ring-rgpd-blue/10">
                <CheckCircleIcon className="h-4 w-4 shrink-0 text-rgpd-blue" />
                <span>Valider les dates de boîtage planifiées</span>
              </li>
            </ul>

            <button
              type="button"
              onClick={() => { void handleSaveAndGoToBoitageQualification(); }}
              disabled={isSaving}
              className="group inline-flex w-full items-center justify-between gap-3 rounded-xl bg-rgpd-blue px-5 py-3.5 text-left text-sm font-semibold text-white shadow-md transition-all hover:bg-rgpd-blue-dark hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span className="flex flex-col gap-0.5">
                <span className="text-[10px] font-medium tracking-wider text-white/70 uppercase">
                  Étape suivante
                </span>
                <span>
                  {isSaving ? 'Enregistrement…' : 'Enregistrer les préférences et aller à la page de configuration du boitage'}
                </span>
              </span>
              <ArrowRightIcon className="h-5 w-5 shrink-0 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </SectionShell>
      )}

      <div className="sticky bottom-4 z-20 flex flex-col gap-2 rounded-2xl border border-gray-200 bg-white/95 px-5 py-4 shadow-xl ring-1 ring-black/5 backdrop-blur-sm sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <span
            className={classNames(
              'inline-flex h-2 w-2 rounded-full',
              isDirty ? 'animate-pulse bg-orange-500' : 'bg-emerald-500',
            )}
            aria-hidden="true"
          />
          {isDirty
            ? 'Modifications non enregistrées'
            : 'Toutes vos préférences sont enregistrées'}
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
          {isDirty && (
            <button
              type="button"
              onClick={handleReset}
              disabled={isSaving}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              Annuler les modifications
            </button>
          )}
          <button
            type="button"
            onClick={() => { void handleSave(); }}
            disabled={!isDirty || isSaving}
            className="rounded-lg bg-rgpd-blue px-5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-rgpd-blue-dark disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSaving ? 'Enregistrement…' : 'Enregistrer les préférences'}
          </button>
        </div>
      </div>

      <PreviewModal
        isOpen={previewTarget !== null}
        target={previewTarget}
        onClose={() => setPreviewTarget(null)}
      />

      <Popup
        isOpen={blocker.state === 'blocked'}
        onClose={handleBlockerStay}
        title="Modifications non enregistrées"
        disableCloseButton={isSaving}
        maxWidth="32rem"
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-slate-700">
            Vous avez des modifications de contenu non enregistrées. Si vous quittez maintenant,
            ces choix seront perdus.
          </p>
          <p className="text-sm text-slate-600">
            Souhaitez-vous enregistrer vos préférences avant de continuer ?
          </p>
          <div className="mt-2 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={handleBlockerStay}
              disabled={isSaving}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              Rester sur la page
            </button>
            <button
              type="button"
              onClick={handleBlockerLeaveWithoutSaving}
              disabled={isSaving}
              className="rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-60"
            >
              Quitter sans enregistrer
            </button>
            <button
              type="button"
              onClick={() => { void handleBlockerSaveAndLeave(); }}
              disabled={isSaving}
              className="rounded-lg bg-rgpd-blue px-5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-rgpd-blue-dark disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSaving ? 'Enregistrement…' : 'Enregistrer et quitter'}
            </button>
          </div>
        </div>
      </Popup>
    </div>
  );
});

ContentChoice.displayName = 'ContentChoice';

export default ContentChoice;
